import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import {
  Alert,
  Button,
  DrawerBody,
  DrawerFooter,
} from '@one-thd/sui-atomic-components';
import { InlineProjectCreation } from '@thd-olt-component-react/project-forms';
import { AddToCoreContext } from '../../core/AddToCoreContext';
import { ItemIconStrip } from '../../common/item-icon-strip/ItemIconStrip';
import { TRACK } from '../../AddToConstants';
import { triggerAnalytics } from '../../AddToHelpers';

export const CreateProjectPreview = ({
  products,
  singleProduct,
  multipleProduct,
  onProjectCreatedAndItemAdded,
  copyLocation,
}) => {
  const { handleClose } = useContext(AddToCoreContext);
  const [successAlert, setSuccessAlert] = useState(null);

  const handleCancel = () => {
    handleClose(undefined, { isUserInitiated: true });
  };

  const resolvedProducts = products.filter((product) => product?.itemId);

  const handleInlineCreationSuccess = ({ projectId, projectName }) => {
    setSuccessAlert({
      itemCount: products.length,
      projectName,
    });

    triggerAnalytics(TRACK.ADD_TO_LIST_ADD_ACTION, {
      name: 'new project project add',
      location: copyLocation || '',
      // Intents carry no itemId, so they are excluded from the sku-based analytics rather
      // than each reporting an undefined sku and collapsing into one unique entry.
      items: resolvedProducts.map((prod) => ({
        quantity: prod.quantity || 1,
        price: prod.pricing?.value || prod.price,
        sku: prod.itemId,
        aiQuantityEstimate: prod?.aiQuantityEstimate ? 'yes' : 'no'
      })),
      projectId,
      uniqueSkuCount: new Set(resolvedProducts.map((prod) => prod.itemId)).size,
      projectAddTotal: products.reduce(
        (total, prod) => total + ((prod.pricing?.value || prod.price || 0) * (prod.quantity || 1)), 0
      ),
      projectStarter: 'copy from your lists',
    });

    if (onProjectCreatedAndItemAdded) {
      onProjectCreatedAndItemAdded({ projectId, projectName });
    }
  };

  return (
    <>
      <DrawerBody>
        {/* Success Alert */}
        {successAlert && (
          <div className="sui-mb-9">
            <Alert
              status="success"
              onClose={() => setSuccessAlert(null)}
              data-testid="create-project-success-alert"
            >
              {successAlert.itemCount} {successAlert.itemCount === 1 ? 'item has' : 'items have'} been
              copied from your Lists.
            </Alert>
          </div>
        )}
        {/* Product Thumbnails */}
        <ItemIconStrip items={products} className="sui-mb-5 sui-mx-9" />

        {/* Inline Project Creation */}
        <InlineProjectCreation
          products={products}
          singleProduct={singleProduct}
          multipleProduct={multipleProduct}
          onSuccess={handleInlineCreationSuccess}
        />
      </DrawerBody>

      <DrawerFooter style={{ boxShadow: '0 1px 8px 0 rgb(0 0 0 / .375)' }}>
        <Button
          variant="secondary"
          fullWidth
          onClick={handleCancel}
          data-testid="create-project-preview-cancel"
        >
          Cancel
        </Button>
      </DrawerFooter>
    </>
  );
};

CreateProjectPreview.displayName = 'CreateProjectPreview';

CreateProjectPreview.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    intentId: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.string,
  })),
  singleProduct: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.string,
  })),
  multipleProduct: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.string,
  })),
  onProjectCreatedAndItemAdded: PropTypes.func,
  copyLocation: PropTypes.string,
};

CreateProjectPreview.defaultProps = {
  products: [],
  singleProduct: [],
  multipleProduct: [],
  onProjectCreatedAndItemAdded: () => { },
  copyLocation: '',
};

CreateProjectPreview.dataModel = extend(
  ItemIconStrip,
  InlineProjectCreation,
);
